import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@mui/material';
import { blue } from '@mui/material/colors';
import { Box, Stack } from '@mui/system';

import WaveUINoDataOverlay from '@/shared/components/utilities/WaveUINoDataOverlay';
import FulfilmentHistory from '@/shared/types/fulfilment/history.type';
import Order from '@/shared/types/order/order.type';
import OrderProduct from '@/shared/types/order/orderProduct.type';

import OrderSummary from './OrderSummary';
import ProductDetails from './ProductDetails';

type ProductDetailsListProps = {
  order: Order;
  history?: FulfilmentHistory;
  activeHistoryPanelItem: {
    id: string;
    isActive: boolean;
  };
  onToggleHistoryPanel: (product: OrderProduct, rect: DOMRect) => void;
};

const ProductDetailsList: FC<ProductDetailsListProps> = ({
  order,
  history,
  activeHistoryPanelItem,
  onToggleHistoryPanel,
}) => {
  const { t } = useTranslation(['orders', 'common']);

  return (
    <Stack
      spacing={2}
      sx={{ py: 1, maxHeight: 500, overflowY: 'auto' }}
      useFlexGap
    >
      {order.products.length > 0 ? (
        <>
          {order.products.map((product) => (
            <Box
              key={product.product_id}
              sx={{
                pr: 2,
                py: 1,
                backgroundColor:
                  activeHistoryPanelItem.id === product.product_id &&
                  activeHistoryPanelItem.isActive
                    ? blue[50]
                    : 'transparent',
                transition: 'background-color 0.3s',
              }}
            >
              <ProductDetails
                product={product}
                historyItem={history}
                getProductItemRect={(rect) =>
                  onToggleHistoryPanel(product, rect)
                }
              />
            </Box>
          ))}
          <Stack
            sx={{
              position: 'sticky',
              bottom: '-8px',
              background: 'white',
            }}
          >
            <Divider sx={{ mt: 2, mb: 1 }} />
            <OrderSummary order={order} />
          </Stack>
        </>
      ) : (
        <WaveUINoDataOverlay size='small' title={t('noProducts')} />
      )}
    </Stack>
  );
};

export default ProductDetailsList;
